<template>
    <article class="chart-card">
        <header class="chart-card-head">
            <h3 class="chart-card-title">{{ meta.label }}</h3>
            <span
                v-if="meta.description"
                class="chart-card-info"
                :title="meta.description"
            >
                <i class="fal fa-info-circle"></i>
            </span>
        </header>
        <div v-if="rows.length" class="chart-card-body">
            <canvas ref="canvas"></canvas>
        </div>
        <div v-else class="chart-card-empty">
            <i class="fal fa-chart-pie"></i>
            <span>{{ $t('statistics.dashboard.empty_metric') }}</span>
        </div>
    </article>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { Chart } from 'chart.js'
import {
    groupByRowLabel,
    formatMetricValue,
    rowValue,
    PALETTE
} from '@/helpers/metricFormat'

export default {
    name: 'PieChartWidget',
    props: {
        meta: { type: Object, required: true },
        value: { default: () => [] }
    },
    data() {
        return { chart: null }
    },
    computed: {
        rows() {
            return Array.isArray(this.value) ? this.value : []
        }
    },
    mounted() {
        this.render()
    },
    beforeDestroy() {
        if (this.chart) this.chart.destroy()
    },
    watch: {
        value: { handler() { this.render() }, deep: true },
        'meta.label'() { this.render() }
    },
    methods: {
        render() {
            this.$nextTick(() => {
                if (!this.$refs.canvas) return
                if (this.chart) this.chart.destroy()
                if (!this.rows.length) return
                const locale = (this.$i18n && this.$i18n.locale) || 'fr-FR'
                const unit = this.meta && this.meta.unit
                const labels = this.rows.map(groupByRowLabel)
                const data = this.rows.map(rowValue)
                const colors = this.rows.map((_, i) => PALETTE[i % PALETTE.length])
                this.chart = new Chart(this.$refs.canvas.getContext('2d'), {
                    type: 'doughnut',
                    data: {
                        labels,
                        datasets: [{
                            data,
                            backgroundColor: colors,
                            borderColor: '#fff',
                            borderWidth: 2,
                            hoverOffset: 6
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '55%',
                        plugins: {
                            legend: {
                                position: 'right',
                                labels: { boxWidth: 10, padding: 12 }
                            },
                            datalabels: { display: false },
                            tooltip: {
                                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                                titleColor: '#fff',
                                titleFont: { size: 12, weight: '600' },
                                bodyColor: '#e2e8f0',
                                bodyFont: { size: 12 },
                                padding: 10,
                                cornerRadius: 6,
                                boxPadding: 4,
                                callbacks: {
                                    label: (ctx) => {
                                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0)
                                        const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0.0'
                                        const formatted = formatMetricValue(ctx.parsed, unit, locale)
                                        return ` ${ctx.label}: ${formatted} (${pct}%)`
                                    }
                                }
                            }
                        }
                    }
                })
            })
        }
    }
}
</script>

<style scoped>
.chart-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 280px;
}

.chart-card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.chart-card-title {
    margin: 0;
    font-size: 0.92rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.chart-card-info {
    color: var(--v2-text-4);
    font-size: 0.85rem;
    cursor: help;
    flex-shrink: 0;
}

.chart-card-info i {
    color: inherit !important;
}

.chart-card-body {
    position: relative;
    flex: 1;
    min-height: 220px;
}

.chart-card-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--v2-text-4);
    gap: 8px;
    font-size: 0.85rem;
}

.chart-card-empty i {
    font-size: 1.6rem;
}
</style>
